// =============================================================================
// components/ui/ScreenTip.tsx — collapsible side "tip bubble"
//
// Instead of a full-width card that pushes screen content down, the tip lives as
// a small floating bubble pinned to the right edge. Tapping it opens the tip in a
// popover card; tapping the X collapses it back to the bubble. "Got it"
// permanently dismisses it for that screen (persisted in SecureStore as tip_<key>)
// so it never shows again. Drop <ScreenTip tipKey="home" title="…" text="…" />
// anywhere in a full-screen screen's tree — it positions itself absolutely.
// =============================================================================
import { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
// MIGRATION (bare RN): expo-secure-store -> the keychain-backed secure-store
// adapter (same getItemAsync/setItemAsync API); @expo/vector-icons -> Icon shim.
import * as SecureStore from '../../services/secure-store';
import { Ionicons } from './Icon';
import { colors, spacing, radius, fonts } from '../../constants/theme';

export default function ScreenTip({
  tipKey,
  title,
  text,
  // Kept for backward-compat with existing call sites; no longer changes layout
  // (the bubble always floats).
  floating: _floating,
}: {
  tipKey: string;
  title: string;
  text: string;
  floating?: boolean;
}) {
  // DISABLED globally (client request — remove the floating "light bubble"
  // everywhere). Kept as a no-op so existing <ScreenTip/> call sites compile
  // without churn; delete the mounts in a later cleanup pass.
  return null;

  // Legacy implementation (intentionally unreachable):
  // null = still loading the seen-flag; false = permanently dismissed (render
  // nothing); true = show the bubble.
  const [visible, setVisible] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const insets = useSafeAreaInsets();

  useEffect(() => {
    let active = true;
    SecureStore.getItemAsync(`tip_${tipKey}`)
      .then((v) => { if (active) setVisible(!v); })
      .catch(() => { if (active) setVisible(true); });
    return () => { active = false; };
  }, [tipKey]);

  if (!visible) return null;

  const dismiss = () => {
    setOpen(false);
    setVisible(false);
    SecureStore.setItemAsync(`tip_${tipKey}`, '1').catch(() => {});
  };

  // Vertical anchor: a little below the header, on the right edge.
  const top = insets.top + 64;

  return (
    <>
      {/* Tap-catcher behind the open card so tapping elsewhere collapses it. */}
      {open && (
        <Pressable style={styles.backdrop} onPress={() => setOpen(false)} accessibilityLabel="Close tip" />
      )}

      {!open ? (
        <TouchableOpacity
          style={[styles.bubble, { top }]}
          onPress={() => setOpen(true)}
          activeOpacity={0.85}
          accessibilityRole="button"
          accessibilityLabel={`Tip: ${title}`}
        >
          <Ionicons name="bulb" size={20} color={colors.white} />
        </TouchableOpacity>
      ) : (
        <View style={[styles.card, { top }]}>
          <View style={styles.cardHeader}>
            <View style={styles.iconWrap}>
              <Ionicons name="bulb-outline" size={16} color={colors.primary} />
            </View>
            <Text style={styles.title} numberOfLines={1}>{title}</Text>
            <TouchableOpacity
              onPress={() => setOpen(false)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="button"
              accessibilityLabel="Collapse tip"
            >
              <Ionicons name="close" size={18} color={colors.muted} />
            </TouchableOpacity>
          </View>
          <Text style={styles.text}>{text}</Text>
          <TouchableOpacity onPress={dismiss} style={styles.gotIt} accessibilityRole="button" accessibilityLabel="Dismiss tip">
            <Text style={styles.gotItText}>Got it</Text>
          </TouchableOpacity>
        </View>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 49,
  },
  bubble: {
    position: 'absolute',
    right: spacing.md,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    shadowColor: colors.primary,
    shadowOpacity: 0.35,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 8,
  },
  card: {
    position: 'absolute',
    right: spacing.md,
    width: 250,
    padding: spacing.md,
    borderRadius: radius.lg,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: colors.primary + '22',
    zIndex: 50,
    shadowColor: '#000',
    shadowOpacity: 0.16,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 8 },
    elevation: 12,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: 9,
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: { ...fonts.semibold, fontSize: 14, color: colors.ink, flex: 1 },
  text: { ...fonts.regular, fontSize: 12.5, color: colors.muted, lineHeight: 17 },
  gotIt: {
    alignSelf: 'flex-end',
    marginTop: spacing.sm,
    paddingVertical: 6,
    paddingHorizontal: spacing.md,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '12',
  },
  gotItText: { ...fonts.semibold, fontSize: 12, color: colors.primary },
});
